import {
  HTMLAttributes,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';

import { Tooltip } from '@components/tooltip';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { ToggleTextButton, ToggleVariant } from './toggle-group';

export type MultiToggleGroupProps<Value extends string | number = string> = {
  onChange(value: Value[]): void;
  children: Array<ReactElement<MultiToggleButtonProps<Value>>>;
  value?: Value[];
  defaultValue?: Value[];
  fullWidth?: boolean;
  minSelected?: number;
};

type DefaultButtonProps = Omit<HTMLAttributes<HTMLButtonElement>, 'value' | 'onClick' | 'children'>;

const compareAscending = (a: string | number, b: string | number) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
};

const MultiToggleContext = createContext<{
  selected: Array<string | number>;
  onToggle(value: string | number): void;
  isAtMinimum: boolean;
}>({
  selected: [],
  onToggle: noop,
  isAtMinimum: false,
});

const Root = <Value extends string | number = string>({
  children,
  defaultValue,
  value: userValue,
  onChange,
  fullWidth = false,
  minSelected = 0,
}: MultiToggleGroupProps<Value>) => {
  const [localValue, setLocalValue] = useState<Value[]>(() => {
    if (userValue !== undefined) return userValue;
    return defaultValue ?? [];
  });

  const actualValue = userValue ?? localValue;
  const isAtMinimum = actualValue.length <= minSelected;

  const onToggle = useCallback(
    (value: string | number) => {
      const isDeselecting = actualValue.includes(value as Value);
      if (isDeselecting && actualValue.length <= minSelected) return;
      const next = isDeselecting
        ? actualValue.filter((item) => item !== value)
        : [...actualValue, value as Value].sort(compareAscending);
      if (userValue === undefined) {
        setLocalValue(next);
      }
      onChange(next);
    },
    [actualValue, minSelected, userValue, onChange]
  );

  const contextValue = useMemo(
    () => ({ selected: actualValue, isAtMinimum, onToggle }),
    [actualValue, isAtMinimum, onToggle]
  );

  return (
    <MultiToggleContext.Provider value={contextValue}>
      <Container $fullWidth={fullWidth}>{children}</Container>
    </MultiToggleContext.Provider>
  );
};

Root.displayName = 'MultiToggleGroup.Root';

export type MultiToggleButtonProps<Value extends string | number> = DefaultButtonProps & {
  text: string;
  value: Value;
  tooltip?: string;
  minSelectedTooltip?: string;
  variant?: ToggleVariant;
};

const ToggleButton = <Value extends string | number>({
  text,
  value,
  tooltip,
  minSelectedTooltip,
  variant = 'blue',
  ...rest
}: MultiToggleButtonProps<Value>) => {
  const { selected, onToggle, isAtMinimum } = useContext(MultiToggleContext);
  const isActive = selected.includes(value);
  const isLastSelected = isActive && isAtMinimum;

  const content = (
    <MultiToggleTextButton
      data-active={isActive}
      data-variant={variant}
      {...rest}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle(value);
      }}
    >
      {text}
    </MultiToggleTextButton>
  );

  const resolvedTooltip = isLastSelected ? minSelectedTooltip : tooltip;
  if (!resolvedTooltip) return content;
  return <Tooltip content={resolvedTooltip}>{content}</Tooltip>;
};

ToggleButton.displayName = 'MultiToggleGroup.Button';

export const MultiToggleGroup = {
  Root,
  Button: ToggleButton,
};

const Container = styled.div<{ $fullWidth: boolean }>((props) => ({
  borderRadius: pxToRem(8),
  overflow: 'hidden',
  display: 'flex',
  flexShrink: 0,
  width: props.$fullWidth ? '100%' : 'max-content',
  gap: pxToRem(1),
}));

const MultiToggleTextButton = styled(ToggleTextButton)({
  cursor: 'pointer',
});
